import React from 'react';
import { bool, shape, arrayOf, string, number } from 'prop-types';
import { Box, Text } from 'theme-ui';
import { Stack } from '@bedrock-layout/stack';
import { Inline } from '@bedrock-layout/inline';
import { formatFlagValue } from 'Utils/bulkCodeFlags';

const STATUS_STYLES = {
  succeeded: { bg: 'successFill', color: 'success' },
  skipped: { bg: 'disabledBackground', color: 'disabledText' },
  failed: { bg: 'errorBackground', color: 'errorText' },
};

function StatusBadge({ status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.skipped;
  return (
    <Box
      as="span"
      sx={{ ...style, px: 2, py: 1, borderRadius: 4, fontWeight: 'bold', fontSize: 1, textTransform: 'capitalize' }}
    >
      {status}
    </Box>
  );
}

StatusBadge.propTypes = { status: string.isRequired };

function BulkCodeFlagResults({ title, response }) {
  if (!response) return null;

  const { dryRun, summary, results } = response;

  return (
    <Stack gutter="md">
      <Inline gutter="lg" align="center">
        <Text variant="section">{title}</Text>
        <Text>
          {dryRun ? 'Would change' : 'Changed'}: {summary.succeeded}, skipped: {summary.skipped}, failed:{' '}
          {summary.failed}
        </Text>
      </Inline>
      <Box as="table" variant="tenant-table">
        <thead>
          <tr>
            <Box as="th" px={3} py={2}>
              Tenant
            </Box>
            <Box as="th" px={3} py={2}>
              Current
            </Box>
            <Box as="th" px={3} py={2}>
              New
            </Box>
            <Box as="th" px={3} py={2}>
              Outcome
            </Box>
            <Box as="th" px={3} py={2}>
              Status
            </Box>
          </tr>
        </thead>
        <tbody>
          {results.map((row) => (
            <tr key={row.identifier}>
              <Box as="td" px={3} py={2}>
                {row.name} <Text as="span" sx={{ color: 'disabledText' }}>({row.identifier})</Text>
              </Box>
              <Box as="td" px={3} py={2}>
                {formatFlagValue(row.previousValue)}
              </Box>
              <Box as="td" px={3} py={2}>
                {formatFlagValue(row.newValue)}
              </Box>
              <Box as="td" px={3} py={2} sx={{ color: row.status === 'failed' ? 'errorText' : 'text' }}>
                {row.message}
                {row.stage ? ` (stage: ${row.stage})` : ''}
              </Box>
              <Box as="td" px={3} py={2}>
                <StatusBadge status={row.status} />
              </Box>
            </tr>
          ))}
        </tbody>
      </Box>
    </Stack>
  );
}

BulkCodeFlagResults.propTypes = {
  title: string.isRequired,
  response: shape({
    dryRun: bool,
    summary: shape({ succeeded: number, skipped: number, failed: number }),
    results: arrayOf(
      shape({
        identifier: string,
        name: string,
        status: string,
        stage: string,
        message: string,
      })
    ),
  }),
};

export default BulkCodeFlagResults;
